/**
 * adumo.js - Adumo Online (Lesaka) v3 pay-in client seam for the standalone
 * tiptag.net app. Ported from the monorepo apps/pay/src/lib/adumo-sdk.ts.
 *
 * Primary path:
 *   mountEmbeddedPayments() - every payment method comes from Adumo's own SDK: one
 *     device-appropriate wallet (Apple Pay or Google Pay) plus Card. Calls
 *     /api/adumoSession.php, which first mints the Omnea allocation token and embeds it
 *     in Adumo's MerchantReference, then harvests the gateway session GUID from
 *     initialisevirtual's 302 and returns { mode:'embedded', sessionToken:GUID,
 *     scriptUrl, gpayFlow }. We load the SDK script,
 *     initClient().initPayments({sessionToken}), then initPaymentMethod() for the wallet
 *     and Card. onSuccess fires IN-PAGE → receipt. No hosted redirect is exported from
 *     this seam.
 *
 * Google Pay used to be hand-rolled here instead (Google's own createButton() plus a
 * direct loadPaymentData() call from tiptag.net). Adumo flagged it: that calls Google
 * directly from our origin while presenting THEIR Google Pay merchant ID, which only
 * works if tiptag.net is registered against that merchant ID, and otherwise fails with
 * OR_BIBED_11. The SDK's default 'handoff' flow makes the same call from
 * gateway.adumoonline.com, an origin Adumo has already registered, so it works from any
 * merchant domain. See GPAY_FLOW in api/adumoSession.php.
 *
 * Amounts are integer CENTS on the wire (Adumo is sent rands server-side).
 *
 * TWO SEPARATE RECONCILIATIONS - don't conflate them:
 *   - TipTag ← the Adumo transaction reference (transactionIndex) surfaced by
 *     onSuccess / the hosted return leg, reconciled against Adumo's report.
 *   - Omnea  ← allocates the funds to the tag holder off the 10-digit Omnea token
 *     embedded in the MerchantReference (see api/merchantReference.php) when
 *     Adumo's webhook reaches Cortex. That is why a session cannot be minted
 *     without a token.
 */

const SESSION_URL = '/api/adumoSession.php';

/**
 * The two values Adumo's SDK accepts for appearance.settings.googlePayFlow. The SDK
 * schema-validates this enum and THROWS on anything else, and that throw would happen
 * inside initPayments' onReady, rejecting the whole mount and taking Card down with
 * Google Pay. adumoSession.php already clamps GPAY_FLOW server-side; clamp again here so
 * a stale cached response or a hand-edited setting can never reach the SDK.
 */
const GPAY_FLOWS = ['handoff', 'in-page'];

function gpayFlowOf(session) {
  const flow = session && session.gpayFlow;
  return GPAY_FLOWS.includes(flow) ? flow : 'handoff';
}

/**
 * Why a session came back 'unavailable'. Two very different causes share the mode:
 *   - a token/config REFUSAL from adumoSession.php (reason set, HTTP 503) - the
 *     hosted + wallet rails go through the same endpoint and will refuse too;
 *   - a gateway/WAF block (no reason, gatewayStatus set) - the hosted mint may
 *     still succeed, so the fallback is worth trying.
 * Returning the reason lets the caller tell those apart instead of guessing.
 */
export const REFUSAL_REASONS = [
  'token_mint_failed', 'token_shape_invalid', 'shortcode_missing', 'reference_too_long',
];

function sessionReason(session) {
  if (session && session.reason) return String(session.reason);
  if (session && session.gatewayStatus) return `gateway-${session.gatewayStatus}`;
  return 'gateway-blocked';
}

/**
 * Extract the authoritative Adumo transaction reference from the SDK onSuccess
 * payload - this is the SDK-only reconciliation key. The gateway transactionIndex
 * is preferred; we fall back across the field names the SDK has used, then the
 * MerchantReference, so a successful payment always carries a usable reference.
 */
function adumoTxnRef(r) {
  if (!r) return '';
  return (
    r.transactionIndex || r._TRANSACTIONINDEX || r.transactionId
    || r.reference || r.ref
    || r.merchantReference || r._MERCHANTREFERENCE
    || ''
  );
}

/**
 * Pick the ONE wallet to show for this device. Returns:
 *   'apple'  - the browser PROVES Apple Pay capability (window.ApplePaySession
 *              exists and canMakePayments() is true). Never inferred from the UA:
 *              macOS Chrome carries an Apple UA but has no ApplePaySession, and the
 *              old UA fallback mounted a dead Apple Pay tile there (GO-LIVE-RUNBOOK
 *              Phase 2.2) - a button the guest taps that can never take a payment.
 *   'google' - every other browser. Adumo's SDK GooglePay element is mounted for this
 *              path and runs its own isReadyToPay check, rendering nothing when the
 *              device cannot pay.
 */
export function pickWallet() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return 'google';

  // Definitive capability check - Safari exposes ApplePaySession. canMakePayments()
  // is true on ANY Apple Pay-capable device/OS, so it's the strongest "apple" signal.
  // This is the ONLY way 'apple' is returned: no capability, no Apple Pay button.
  try {
    if (window.ApplePaySession && typeof window.ApplePaySession.canMakePayments === 'function'
        && window.ApplePaySession.canMakePayments()) {
      return 'apple';
    }
  } catch (e) { /* ApplePaySession can throw in cross-origin frames - ignore */ }

  // No proven Apple Pay capability: offer the Google rail and let Google's
  // isReadyToPay() decide. The SDK's GooglePay element runs that check itself and
  // renders nothing when it returns false, so Card remains available and there is no
  // dead wallet tile.
  return 'google';
}

/**
 * Adumo's own auto-initiated status screen renders a native <dialog> into
 * document.body and opens it modally. A modal <dialog> makes the ENTIRE document
 * inert per the HTML spec - not just its own footprint - so if we abandon the SDK
 * path (blocked / failed / mount error) without closing it, every control on the
 * page (including any fallback UI) silently stops responding to clicks. Call this
 * whenever mountEmbeddedPayments() is about to return anything other than
 * 'embedded', so the rest of the page stays usable no matter what the caller does
 * with that state.
 */
function teardownStraySdkDialogs() {
  try {
    const dialogs = document.querySelectorAll('dialog');
    dialogs.forEach((d) => {
      try { d.close(); } catch (e) { /* already closed */ }
      d.remove();
    });
    return dialogs.length;
  } catch (e) { return 0; /* non-DOM environment - nothing to tear down */ }
}

/* ── Card-dialog scale-to-fit ────────────────────────────────────────────────
 * Measured on staging (2026-08-05): the SDK's card-payment iframe page does
 * NOT reflow. It lays out at a fixed content width and the SDK reports that
 * width by writing an inline `width: 472px` (value varies per build) onto the
 * iframe after the inner page loads - the SAME value whether the iframe was
 * 565px or 374px wide at load. An earlier note claiming the page "reflows
 * cleanly down to ≤304px" is wrong for this dialog. On a phone the <dialog>
 * is clamped by the UA rule `max-width: calc(100% - 38px)` (~336px at a 374px
 * viewport), so the ~472px inner form clips at the edges.
 *
 * CSS alone cannot fix this: the inner document is cross-origin and a
 * unitless scale factor cannot be computed in CSS. So when the dialog is
 * narrower than the inner page's natural width, lay the iframe out AT that
 * natural width and transform:scale() it down to fit - the form renders
 * complete and correctly proportioned, just smaller.
 *
 * Robustness requirements (the "sometimes big, sometimes clipped" symptom):
 *   - the SDK (re)writes the iframe's style attribute asynchronously (its
 *     resize messages), clobbering anything we set inline → MutationObserver
 *     re-applies;
 *   - dialogs are re-created per open → observe document.body childList;
 *   - our values must beat BOTH the SDK's inline writes and our own
 *     stylesheet baseline (App.css `dialog.Dialog .DialogBody iframe
 *     { width:100% !important … }`) → inline `!important` (style-attribute
 *     important outranks stylesheet important);
 *   - never throw into the payment path → everything try/caught;
 *   - idempotent writes (skip when already applied) so our own mutations
 *     don't loop the observer.
 */
let cardDialogFitterInstalled = false;

/** Set an inline !important property only if not already set to that value. */
function setImportant(el, prop, val) {
  if (el.style.getPropertyValue(prop) === val
      && el.style.getPropertyPriority(prop) === 'important') return;
  el.style.setProperty(prop, val, 'important');
}

function fitOneCardDialog(dlg) {
  const body = dlg.querySelector('.DialogBody');
  const ifr = body && body.querySelector('iframe');
  if (!ifr) return;

  // Natural width = the px width written on the iframe's inline style. The
  // SDK writes the inner page's reported content width there; our own writes
  // are either that same natural width or '100%', so any px value is a
  // trustworthy signal. Remember it (dataset) for passes where the current
  // inline value is '100%'.
  const inlineW = ifr.style.width || '';
  let natural = /px$/.test(inlineW) ? parseFloat(inlineW) : 0;
  if (natural > 0) ifr.dataset.ttNaturalWidth = String(natural);
  else natural = parseFloat(ifr.dataset.ttNaturalWidth || '') || 0;

  const avail = dlg.getBoundingClientRect().width;
  if (!natural || !avail) return; // SDK hasn't reported yet - CSS 100% baseline applies

  if (avail + 1 >= natural) {
    // Enough room (desktop): baseline behavior - iframe tracks the dialog.
    setImportant(ifr, 'width', '100%');
    setImportant(ifr, 'max-width', '100%');
    ifr.style.removeProperty('transform');
    ifr.style.removeProperty('transform-origin');
    if (body.style.getPropertyValue('height')) body.style.removeProperty('height');
  } else {
    // Not enough room (phone): lay out at natural width, scale down to fit.
    const scale = avail / natural;
    setImportant(ifr, 'width', `${natural}px`);
    setImportant(ifr, 'max-width', 'none');
    setImportant(ifr, 'transform', `scale(${scale})`);
    setImportant(ifr, 'transform-origin', 'top left');
    // transform doesn't shrink the layout box - compensate the body height
    // so the dialog doesn't keep the unscaled empty space below the form.
    // offsetHeight is the UNscaled layout height (rects would be scaled).
    const h = ifr.offsetHeight;
    if (h) setImportant(body, 'height', `${Math.ceil(h * scale)}px`);
  }
}

function fitAllCardDialogs() {
  try {
    const dialogs = document.querySelectorAll('dialog.Dialog');
    for (let i = 0; i < dialogs.length; i += 1) fitOneCardDialog(dialogs[i]);
  } catch (e) { /* never let sizing break the payment path */ }
}

/** Install the fitter once per page. Safe to call repeatedly. */
function installCardDialogFitter() {
  if (cardDialogFitterInstalled) return;
  if (typeof document === 'undefined' || !document.body
      || typeof MutationObserver === 'undefined') return;
  cardDialogFitterInstalled = true;
  try {
    const mo = new MutationObserver(fitAllCardDialogs);
    // childList: dialog/iframe creation; attributes(style): the SDK's async
    // inline-style writes on the iframe (and dialog open/close), which are
    // exactly the writes that clobber ours.
    mo.observe(document.body, {
      childList: true, subtree: true,
      attributes: true, attributeFilter: ['style', 'open', 'class'],
    });
    window.addEventListener('resize', fitAllCardDialogs);
    fitAllCardDialogs();
  } catch (e) {
    cardDialogFitterInstalled = false; // best effort - retry on next mount
  }
}

let scriptLoadingPromise = null;

/** Lazy-load the Adumo JS client into <head> exactly once. */
function loadAdumoScript(scriptUrl) {
  if (typeof window !== 'undefined' && window.adumo) return Promise.resolve();
  if (!scriptUrl) return Promise.reject(new Error('No Adumo SDK script URL'));
  if (scriptLoadingPromise) return scriptLoadingPromise;

  scriptLoadingPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = scriptUrl;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptLoadingPromise = null; // Reset so next attempt can retry
      reject(new Error(`Failed to load Adumo SDK from ${scriptUrl}`));
    };
    document.head.appendChild(script);
  });
  return scriptLoadingPromise;
}

async function createSession(body) {
  const res = await fetch(SESSION_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  // Read the body REGARDLESS of status. adumoSession.php fails CLOSED on a token
  // problem with HTTP 503 + { mode:'unavailable', reason } - bailing on !res.ok
  // discarded that body, so the caller could neither back off nor say why, and
  // every retry re-hit Omnea's token API. A genuine platform error page isn't
  // JSON → null → the existing 'failed' path, unchanged.
  const session = await res.json().catch(() => null);
  return (session && typeof session === 'object') ? session : null;
}

/**
 * Mount Adumo's inline wallet buttons (Google Pay + Apple Pay) for the given
 * amount. Returns { state } where state is:
 *   'embedded' - SDK mounted; Adumo's buttons render in the supplied refs
 *   'blocked'  - the application is provisioned with autoRedirectEnabled=true, so the
 *                session is auto-initiated and the SDK renders its transaction-status
 *                screen instead of the payment tiles. Inline cannot work until Adumo
 *                sets autoRedirectEnabled=false; the caller shows an inline blocker.
 *   'stub'     - no live creds server-side; caller shows the stub CTA
 *   'unavailable' - gateway/WAF blocked the mint (e.g. 403)
 *   'failed'   - session or SDK failure; caller shows an inline blocker
 *
 * The session is amount-specific - re-call after the amount changes (debounce).
 */
export async function mountEmbeddedPayments(args) {
  const key = `${args.payType}:${args.amountCents}`;
  console.log(`[tiptag][pay] mint start key=${key}`);
  // The SDK renders its card <dialog> lazily (on Card-tile tap) - make sure the
  // scale-to-fit observer is watching before any dialog can exist. Idempotent.
  installCardDialogFitter();
  try {
    const session = await createSession({
      tagId: args.tagId,
      amountCents: args.amountCents,
      currency: 'ZAR',
      payType: args.payType,
      paymentContext: args.paymentContext,
      returnOrigin: window.location.origin,
    });
    if (!session) { console.log(`[tiptag][pay] mode=failed reason=no-session key=${key}`); return { state: 'failed' }; }
    if (session.stub || session.mode === 'stub') { console.log(`[tiptag][pay] mode=stub key=${key}`); return { state: 'stub' }; }
    // Edge/WAF blocked the session mint (e.g. 403) - surface a friendly state.
    if (session.mode === 'unavailable') {
      const reason = sessionReason(session);
      console.log(`[tiptag][pay] mode=unavailable reason=${reason} key=${key}`);
      return { state: 'unavailable', reason };
    }
    if (session.mode !== 'embedded' || !session.sessionToken) { console.log(`[tiptag][pay] mode=failed reason=bad-session-shape key=${key}`, session); return { state: 'failed' }; }
    console.log(`[tiptag][pay] session minted sessionToken=${session.sessionToken} key=${key}`);

    await loadAdumoScript(session.scriptUrl);
    console.log('[tiptag][pay] adumo-js-client.js loaded');
    const w = window;
    if (!w || !w.adumo || !w.adumo.initClient) {
      console.log(`[tiptag][pay] mode=failed reason=sdk-global-missing key=${key}`);
      teardownStraySdkDialogs();
      return { state: 'failed' };
    }

    if (args.googlePayRef) args.googlePayRef.innerHTML = '';
    if (args.applePayRef) args.applePayRef.innerHTML = '';
    if (args.cardRef) args.cardRef.innerHTML = '';
    args.paymentsRef.innerHTML = '';
    const client = w.adumo.initClient();
    const gpayFlow = gpayFlowOf(session);

    let blockedCheckInterval = null;
    await new Promise((resolve, reject) => {
      let isSettled = false;

      const finishReady = () => {
        if (isSettled) return;
        isSettled = true;
        clearInterval(blockedCheckInterval);
        resolve();
      };

      const finishReject = (err) => {
        if (isSettled) return;
        isSettled = true;
        clearInterval(blockedCheckInterval);
        reject(err);
      };

      // Periodic check for the transaction-status iframe to reject early (rather than waiting for 15s timeout)
      blockedCheckInterval = setInterval(() => {
        try {
          if (args.paymentsRef.querySelector('iframe[src*="transaction-status"]')) {
            finishReject(new Error('blocked-autoRedirectEnabled'));
          }
        } catch (e) { /* ignore */ }
      }, 100);

      client.initPayments({
        ref: args.paymentsRef,
        sessionToken: session.sessionToken,
        onReady: () => {
          console.log('[tiptag][pay] SDK onReady - mounting wallet + card tiles');
          try {
            // Mount ONE wallet tile and the card tile in-page. Each initPaymentMethod
            // renders a CLICKABLE TILE only. Clicking a tile calls the SDK's
            // setSelectedPaymentType; the SDK then renders that method's FORM into the
            // initPayments ref (args.paymentsRef) - which is why paymentsRef must be
            // VISIBLE. Wallets fire a native sheet/popup; Card renders its form iframe in
            // paymentsRef. All report results through the onSuccess/onFailure callbacks.
            const wallet = pickWallet();
            console.log(`[tiptag][pay] initPaymentMethod wallet=${wallet} gpayFlow=${gpayFlow}`);
            if (wallet === 'apple') {
              client.initPaymentMethod({ ref: args.applePayRef, type: 'ApplePay' });
            } else if (args.googlePayRef) {
              // Adumo passes these options to Google's official button. White provides
              // contrast on our dark page; plain preserves the existing Google Pay label.
              // Configure the 12px radius here so the SDK applies it to both the button
              // and its PaymentMethod wrapper, without CSS clipping or radius overrides.
              // Keep the panel at the existing Card tile's 52px height with no padding.
              client.initPaymentMethod({
                ref: args.googlePayRef,
                type: 'GooglePay',
                appearance: {
                  styles: {
                    PaymentMethod: {
                      backgroundColor: 'transparent',
                      padding: 0,
                      height: '52px',
                    },
                  },
                  settings: {
                    googlePayFlow: gpayFlow,
                    googlePayStyle: 'google',
                    googlePayButtonConfig: {
                      buttonColor: 'white',
                      buttonType: 'plain',
                      buttonRadius: 12,
                    },
                  },
                },
                // Element-level errors are PRE-payment: a blocked popup on the handoff
                // flow, or Google being unavailable. Before the mount settles they mean
                // the wallet never came up, and the caller's fallback rail handles that,
                // so they must not be dressed up as a failed payment to the guest.
                onError: (msg) => {
                  const text = (msg && msg.message) || msg || 'Google Pay is unavailable';
                  console.log('[tiptag][pay] GooglePay element error', msg);
                  if (isSettled) args.onFailure(String(text));
                },
              });
            }
            // In-page card (exact casing 'Card'): tile → selects Card → form renders in
            // paymentsRef. No redirect, no modal - success fires CARD_PAYMENT_SUCCESS.
            if (args.cardRef) {
              console.log('[tiptag][pay] initPaymentMethod type=Card');
              client.initPaymentMethod({ ref: args.cardRef, type: 'Card' });
            }
            finishReady();
          } catch (e) {
            finishReject(e instanceof Error ? e : new Error('method mount failed'));
          }
        },
        // SDK-native in-page callbacks - the SINGLE source of truth for every method
        // (Google Pay, Apple Pay, Card). The SDK fires these after Adumo's callback page
        // postMessages the result back to this window. SDK-only reconciliation: capture
        // the transactionIndex from the success payload.
        // Log the raw payload AND the reference we derived from it: the SDK has not been
        // consistent about which field carries the transaction reference, so when Omnea
        // or Adumo ask us to trace a payment, "what we extracted" is the useful half.
        onSuccess: (r) => {
          const txnRef = adumoTxnRef(r);
          console.log('[tiptag][pay] onSuccess', r);
          console.log(`[tiptag][pay] txnRef=${txnRef}`);
          args.onSuccess(txnRef);
        },
        // A failure BEFORE the mount settles is the SDK failing to START - no card has
        // been entered and no payment was attempted. Routing it to args.onFailure told
        // the guest "Your payment could not be completed" the moment the page loaded,
        // and each one bumped the retry counter, so the app re-minted sessions in a
        // loop. Reject the mount instead: the caller drops to the hosted fallback - a
        // working card rail - and the guest sees payment buttons rather than an alarming
        // error about a payment they never made.
        //
        // This is what production hits today. Its gateway serves an older
        // adumo-js-client that gates on `hasInitiated` (true on every fresh session
        // while autoRedirectEnabled=true) and errors out, whereas staging's newer build
        // gates on `transactionPendingOrComplete` and mounts fine - same config on both
        // applications, verified 6 Aug. So with this change one build behaves correctly
        // in both places: staging renders the in-page tiles, production quietly uses
        // the card fallback until Adumo updates the production bundle.
        //
        // AFTER the mount settles the SDK is live and a failure is a real payment
        // outcome, so it reaches the guest unchanged.
        onFailure: (e) => {
          const msg = (e && e.message) || 'Payment failed';
          console.log('[tiptag][pay] onFailure', e);
          if (!isSettled) { finishReject(new Error('sdk-init-failed: ' + msg)); return; }
          args.onFailure(msg);
        },
        onCancel: () => { console.log('[tiptag][pay] onCancel'); args.onCancel(); },
        onError: (e) => {
          const msg = (e && e.message) || 'Payment error';
          console.log('[tiptag][pay] onError', e);
          if (!isSettled) { finishReject(new Error('sdk-init-error: ' + msg)); return; }
          args.onFailure(msg);
        },
      });
      setTimeout(() => finishReject(new Error('SDK ready timeout')), 15000);
    });

    console.log(`[tiptag][pay] mode=embedded reason=ready key=${key}`);
    return { state: 'embedded' };
  } catch (e) {
    console.log('[tiptag][pay] mode=failed reason=exception', e);
    teardownStraySdkDialogs();
    if (e && e.message === 'blocked-autoRedirectEnabled') {
      return { state: 'blocked' };
    }
    return { state: 'failed' };
  }
}

/**
 * Mint an Adumo hosted-card session for the in-page iframe fallback.
 * This function NEVER navigates. The caller may put redirectUrl into an iframe/modal.
 */
export async function createHostedSession(args) {
  try {
    const session = await createSession({
      tagId: args.tagId,
      amountCents: args.amountCents,
      currency: 'ZAR',
      payType: args.payType,
      paymentContext: args.paymentContext,
      returnOrigin: window.location.origin,
      preferHosted: true,
    });
    if (!session) return { state: 'failed' };
    if (session.stub || session.mode === 'stub') return { state: 'stub' };
    if (session.mode === 'redirect' && session.redirectUrl && session.rail !== 'omnea') {
      return { state: 'ok', url: session.redirectUrl, rail: session.rail || 'adumo_iframe' };
    }
    if (session.mode === 'unavailable') return { state: 'unavailable', reason: sessionReason(session) };
    return { state: 'failed' };
  } catch (e) {
    console.error('[tiptag][pay] createHostedSession failed', e);
    return { state: 'failed' };
  }
}
